import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';

import { useAuth } from '../context/AuthContext';
import { FROM_REFER_PARAM, type InviteReturn } from '../hooks/useGmailInvite';
import { gmailInviteService, INVITE_TOKEN_KEY } from '../services/gmailInviteService';
import { watchFirstUse } from '../utils/inviteFirstUse';

import { InviteOfferModal } from './InviteOffer';
import { InvitePeopleModal } from './InvitePeopleModal';
import { ReferralModal } from './ReferralModal';

const RETURN_PARAMS = ['gmail_invites', 'gmail_invites_error'];
/** ``permission``: Google's consent page was declined or a needed box left unticked. */
const REASON_PARAM = 'gmail_invites_reason';
/** A project invite's button lands on the invitation with its Gmail invite (the click was counted on the way). */
const LINK_PARAM = 'gmail_invite';
const STRIPPED_PARAMS = [...RETURN_PARAMS, REASON_PARAM, FROM_REFER_PARAM, LINK_PARAM];

const takeToken = (): string | null => {
  try {
    const token = localStorage.getItem(INVITE_TOKEN_KEY);

    localStorage.removeItem(INVITE_TOKEN_KEY);

    return token;
  } catch {
    // Blocked storage: the email match still counts the signup.
    return null;
  }
};

/** Google lands back with a full page load, so the parameters are read once, when the app starts. */
const readReturn = () => {
  const params = new URLSearchParams(window.location.search);

  return {
    open: RETURN_PARAMS.some((name) => params.has(name)),
    notice: params.get('gmail_invites_error'),
    permission: params.get(REASON_PARAM) === 'permission',
    // Invite all was pressed in Refer & earn or in the offer after sign-in, so that is the window to reopen.
    from: params.get(FROM_REFER_PARAM) as InviteReturn | null,
  };
};

const useStripReturnParams = () => {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const params = new URLSearchParams(location.search);

    if (!STRIPPED_PARAMS.some((name) => params.has(name))) return;
    STRIPPED_PARAMS.forEach((name) => params.delete(name));
    const search = params.toString() ? `?${params.toString()}` : '';

    navigate({ pathname: location.pathname, search, hash: location.hash }, { replace: true });
  }, [location.search, location.pathname, location.hash, navigate]);
};

const storedToken = (): string | null => {
  try {
    return localStorage.getItem(INVITE_TOKEN_KEY);
  } catch {
    return null;
  }
};

/**
 * Keep a project invite's token like the referral page keeps an 8080.AI invite's (so its signup is counted), and
 * watch for the first real use of the site after an invite's button, also on a later visit. Runs before the claim,
 * which takes the token once someone is signed in.
 */
const useFirstUse = () => {
  useEffect(() => {
    const fromLink = new URLSearchParams(window.location.search).get(LINK_PARAM);

    if (fromLink) {
      try {
        localStorage.setItem(INVITE_TOKEN_KEY, fromLink);
      } catch {
        // Blocked storage: the first use is still reported for this page.
      }
    }
    const token = fromLink || storedToken();

    if (token) watchFirstUse(token);
  }, []);
};

/** Count the signup that followed an invite's link, once the new account is signed in. */
const useInviteClaim = (signedIn: boolean) => {
  useEffect(() => {
    const token = signedIn ? takeToken() : null;

    if (token) void gmailInviteService.claim(token).catch(() => undefined);
  }, [signedIn]);
};

const projectFromPath = (): string | undefined => window.location.pathname.match(/^\/planning\/([^/?#]+)/)?.[1];

interface ReturnWindowProps {
  from: InviteReturn | null;
  open: boolean;
  notice: string | null;
  permission: boolean;
  onClose: () => void;
}

/**
 * The window Google's answer reopens: the offer after sign-in, Refer & earn (Invite all), or Invite people. Invite all
 * pressed again there (Try again) counts as started from Google's return.
 */
const ReturnWindow: React.FC<ReturnWindowProps> = ({ from, open, notice, permission, onClose }) => {
  if (from === 'offer') {
    return open ? createPortal(
      <InviteOfferModal notice={notice} onClose={onClose} permission={permission} source="google_return" />,
      document.body,
    ) : null;
  }

  return from === 'refer'
    ? <ReferralModal gmailNotice={notice} gmailPermission={permission} initialMode="refer" isOpen={open}
      onClose={onClose} projectId={projectFromPath()} source="google_return" />
    : <InvitePeopleModal isOpen={open} notice={notice} onClose={onClose} projectId={projectFromPath()} />;
};

/**
 * Back from Google's consent page (``?gmail_invites=<batch>`` or ``?gmail_invites_error=``): reopen Invite people,
 * where the contacts are ready to choose from, or the window Invite all was pressed in (Refer & earn, or the offer
 * after sign-in), where it shows its progress, or what to tick on Google's page when its permission was not given.
 */
export const GmailInviteReturn: React.FC = () => {
  const { user } = useAuth();
  const [state, setReturn] = useState(readReturn);
  const close = () => setReturn({ ...state, open: false, notice: null, permission: false });

  useFirstUse();
  useStripReturnParams();
  useInviteClaim(Boolean(user));

  return <ReturnWindow {...state} onClose={close} />;
};
